/*    
<summary>
  This functional component "AddOptionMenu" defines the props like initial values and functions for the child form component.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import { cloneDeep } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import {
  registrationTypeOptions,
} from "../../../../constants/options.constant";
import { useStore } from "../../../../contexts/store-provider";
import { formatMessage } from "../../../../translations/format-message";
import AddEditOptionMenuForm from "../forms/add-edit-option-menu-form";
import { IAddOptionMenu } from "../../../../models/forms/IAddOptionMenu";
import {
  getBusinessTypeIdFromLocalStorage,
  getStoreIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../../../helpers/localstorage-helper";
import userTypeEnum from "../../../../constants/enums/user-type-enum";
import { ActionType } from "../../../../constants/enums/action-type-enum";
import { ModuleType } from "../../../../constants/enums/module-type-enum";
import { isOperationPermittedRoleBase } from "../../../../helpers/action-permission-helper";

interface IProps {
  id: number;
  modalClosed: () => void;
}

const AddEditOptionMenu = (props: IProps) => {
  const { optionMenuStore, masterManagementStore, treatmentManagementStore } =
    useStore();
  const {
    inProgress,
    GetOptionMenuByIdService,
    resetAddUpdateOptionMenuState,
    resetOptionMenuDetail,
    optionmenuState,
    optionmenuDetail,
    addUpdateOptionMenuState,
    AddOptionMenuService,
    UpdateOptionMenuService,
    deleteOptionMenuState,
    DeleteOptionMenuService,

    resetDeleteOptionMenu,
  } = optionMenuStore;

  const { GetTreatmentListSummaryService, getTreatmentSummary } =
    treatmentManagementStore;
  const {
    GetAllTimeService,
    getAllTaxOptions,
    getAllTimeOptions,
    GetAllTaxOptionService,
    getAllBusinessTypes,
    GetAllBusinessTypeService,
  } = masterManagementStore;

  const initialValues: IAddOptionMenu = optionmenuDetail;

  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered
   * while a dependency of the useEffect has changed.
   */
  useEffect(() => {
    GetAllBusinessTypeService();
    let userType = getUserTypeFromLocalStorage();
    if (userTypeEnum[userTypeEnum.SuperAdmin] !== userType) {
      GetAllTaxOptionService();
      GetAllTimeService();
    }
    if (props.id > 0) {
      GetOptionMenuByIdService(props.id);
    }
  }, [
    GetAllBusinessTypeService,
    GetAllTaxOptionService,
    GetAllTimeService,
    GetOptionMenuByIdService,
    props.id,
  ]);

  useEffect(() => {
    let userType = getUserTypeFromLocalStorage();
    if (
      userTypeEnum[userTypeEnum.SuperAdmin] === userType &&
      initialValues &&
      Number(initialValues.BusinessTypeId) > 0
    ) {
      GetAllTaxOptionService(initialValues.BusinessTypeId);
      GetAllTimeService(initialValues.BusinessTypeId);
    }
  }, [GetAllTaxOptionService, GetAllTimeService, initialValues.BusinessTypeId]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateOptionMenuSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateOptionMenuState.success) {
      if (props.id === -1) toast.success(formatMessage("added_success"));
      else toast.success(formatMessage("updated_success"));
      onModalClosed();
    }
  }, [addUpdateOptionMenuState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "optionmenuDetailError" of the useEffect changed.
   */
  useEffect(() => {
    if (optionmenuState.error) {
      toast.error(formatMessage(optionmenuState.error));
    }
  }, [optionmenuState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateOptionMenuError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateOptionMenuState.error) {
      toast.error(formatMessage(addUpdateOptionMenuState.error));
      resetAddUpdateOptionMenuState();
    }
  }, [addUpdateOptionMenuState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "deleteOptionMenuSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (deleteOptionMenuState.success) {
      toast.success(formatMessage("deleted_success"));
      onDeleteModalClosed();
    }
  }, [deleteOptionMenuState.success]);

    useEffect(() => {
      if (deleteOptionMenuState.error) {
        toast.error(formatMessage(deleteOptionMenuState.error));
        resetDeleteOptionMenu();
        props.modalClosed();
      }
    }, [deleteOptionMenuState.error, resetDeleteOptionMenu]);

  /**
   * This function adds the optionmenu when "Id < 0" and updates the Existing optionmenu when Id > 0 by providing form values to the
   * correponding function
   */
  const onSubmitForm = (values: any) => {
    let tempValues = cloneDeep(values);
    tempValues.OptionName = values.OptionName.trim();
    if (props.id < 0) AddOptionMenuService(tempValues);
    else UpdateOptionMenuService(props.id, tempValues);
  };

  /**
   * This function closes the pop up modal and reset getoptionmenudetails, addUpdate treatement observables (success, inprogress
   * and error).
   */
  const onModalClosed = () => {
    resetOptionMenuDetail();
    props.modalClosed();
  };

  const handleDeleteSubmitHandler = () => {
    DeleteOptionMenuService(props.id);
  };

  /**
   * This function closes the pop up modal and reset getoptionmenudetails, delete optionmenu observables (success, inprogress
   * and error).
   */
  const onDeleteModalClosed = () => {
    resetOptionMenuDetail();
    props.modalClosed();
  };

  return (
    <React.Fragment>
      <AddEditOptionMenuForm
        initialValues={initialValues}
        id={props.id}
        businessTypeOptions={getAllBusinessTypes}
        timeOptions={getAllTimeOptions}
        taxOptions={getAllTaxOptions}
        treatmentOptions={getTreatmentSummary}
        submitHandler={onSubmitForm}
        onModalClosed={onModalClosed}
        registrationTypeOptions={registrationTypeOptions}
        getOptionMenuLoading={
          optionmenuState.inProgress ||
          addUpdateOptionMenuState.inProgress ||
          inProgress
        }
        handleDeleteSubmitHandler={handleDeleteSubmitHandler}
        deleteActionLoading={deleteOptionMenuState.inProgress}
        viewOnly={
          props.id > 0
            ? !isOperationPermittedRoleBase(
                ModuleType.StoreManagement,
                ActionType.Add
              )
            : false
        }
      />
    </React.Fragment>
  );
};

export default observer(AddEditOptionMenu);
